import React, { useEffect, useState } from "react";
import {Container,Row,Col,Form,} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { getSubjectTeacher } from "../../store/slices/subjectteacher";
import { addActivity } from "../../store/slices/activity";
import { uploadImage } from "../../utils/helpers/fileupload";
import { MdDelete } from "react-icons/md";

export default function AddActivity() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem("user"));
    const token = user?.data?.accessToken;

   
    const [subjectTeachers, setSubjectTeachers] = useState([]);

    const [standardId, setStandardId] = useState("");
    const [sectionId, setSectionId] = useState("");
    const [title, setTitle] = useState("");
    const [date, setDate] = useState("");
    const [files, setFiles] = useState([]);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* >>>>>>>>> Standard & Section ------------------ */
    useEffect(() => {
        dispatch(getSubjectTeacher())
            .unwrap()
            .then((res) => {
                setSubjectTeachers(res?.data || []);
            })
            .catch(() => { });
    }, [dispatch]);

    /* ------------------ Dropdown Options >>>>>>>>>>>> */
    const standardOptions = [
        ...new Map(
            subjectTeachers.map((i) => [
                i.standardId?._id,
                i.standardId,
            ])
        ).values(),
    ];

    const sectionOptions = subjectTeachers.filter(
        (i) => i.standardId?._id === standardId
    );

    /* >>>>>>>>>>>>> File Upload  ------------------ */
    const handleFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files);

        if (files.length + selectedFiles.length > 3) {
            setErrors((prev) => ({
                ...prev,
                files: "Maximum 3 images allowed",
            }));
            return;
        }

        setErrors((prev) => ({ ...prev, files: "" }));
        setIsSubmitting(true);

        try {
            let uploadedUrls = [];

            for (const file of selectedFiles) {
                const url = await uploadImage(file, token);
                if (url) uploadedUrls.push(url);
            }

            setFiles((prev) => [...prev, ...uploadedUrls]);
        } catch {
            setErrors((prev) => ({
                ...prev,
                files: "Failed to upload image",
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ------------------ Remove File >>>>> */
    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    /* <<<<<<<<<<< Submit ------------------ */
    const handleSubmit = () => {
        const newErrors = {};

        if (!standardId) newErrors.standardId = "Please select standard";
        if (!sectionId) newErrors.sectionId = "Please select section";
        if (!title.trim()) newErrors.title = "Please enter title";
        if (!date) newErrors.date = "Please select date";
        if (files.length === 0) newErrors.files = "Please upload at least one image";

        setErrors(newErrors);
        if (Object.keys(newErrors).length) return;

        const payload = {
            standardId,
            sectionId,
            title,
            date,
            files,
        };

        setIsSubmitting(true);

        dispatch(addActivity(payload))
            .unwrap()
            .then(() => {
                navigate("/teacher/activity");
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <Container fluid className="px-4 w-100">
            {/* Header */}
            <div className="staff-header mb-4">
                <div className="staff-breadcrumb">
                    <Link to="/teacher/home" className="crumb home">
                        Home
                    </Link>
                    <span className="crumb current">Add Activity</span>
                </div>
            </div>

            {/* Main Card */}
            <div
                className="w-100"
                style={{
                    background: "#ffffff",
                    borderRadius: "20px",
                    padding: "24px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    margin: "0 auto",
                }}
            >
                <h5 className="mb-4">Add New Activity</h5>

                <Row>
                    {/* Standard */}
                    <Col md={6} className="mb-3">
                        <Form.Label>Standard *</Form.Label>
                        <Form.Select
                            value={standardId}
                            onChange={(e) => {
                                setStandardId(e.target.value);
                                setSectionId("");
                                setErrors({ ...errors, standardId: "" });
                            }}
                        >
                            <option value="">Select Standard</option>
                            {standardOptions.map((std) => (
                                <option key={std._id} value={std._id}>
                                    {std.name}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.standardId && (
                            <small className="text-danger">{errors.standardId}</small>
                        )}
                    </Col>

                    {/* Section */}
                    <Col md={6} className="mb-3">
                        <Form.Label>Section *</Form.Label>
                        <Form.Select
                            value={sectionId}
                            disabled={!standardId}
                            onChange={(e) => {
                                setSectionId(e.target.value);
                                setErrors({ ...errors, sectionId: "" });
                            }}
                        >
                            <option value="">Select Section</option>
                            {sectionOptions.map((sec) => (
                                <option key={sec.sectionId?._id} value={sec.sectionId?._id}>
                                    {sec.sectionId?.name}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.sectionId && (
                            <small className="text-danger">{errors.sectionId}</small>
                        )}
                    </Col>

                    {/* Title */}
                    <Col md={12} className="mb-3">
                        <Form.Label>Title *</Form.Label>
                        <Form.Control
                            value={title}
                            placeholder="Enter activity title"
                            onChange={(e) => {
                                setTitle(e.target.value);
                                setErrors({ ...errors, title: "" });
                            }}
                        />
                        {errors.title && (
                            <small className="text-danger">{errors.title}</small>
                        )}
                    </Col>

                    {/* Date */}
                    <Col md={6} className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control
                            type="date"
                            value={date}
                            onChange={(e) => {
                                setDate(e.target.value);
                                setErrors({ ...errors, date: "" });
                            }}
                        />
                        {errors.date && (
                            <small className="text-danger">{errors.date}</small>
                        )}
                    </Col>

                    {/* Files */}
                    <Col md={6} className="mb-3">
                        <Form.Label>Upload Images (max 3) *</Form.Label>
                        <Form.Control
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                        {errors.files && (
                            <small className="text-danger">{errors.files}</small>
                        )}

                        {/* File Preview */}
                        {files.length > 0 && (
                            <Col md={12} className="mb-3 my-2 d-flex flex-row" >
                                {files.map((_, i) => (
                                    <div
                                        key={i}
                                        className="d-flex "
                                        style={{backgroundColor:"whitesmoke",padding:"2px",marginRight:"5px",borderRadius:"5px"}} >
                                        <span>File {i + 1}</span>
                                        <span
                                            style={{ cursor: "pointer", color: "red" }}
                                            onClick={() => removeFile(i)}
                                            className="mx-2" >
                                           <MdDelete size={15} />
                                        </span>
                                    </div>
                                ))}
                            </Col>
                        )}

                    </Col>


                    {/* Buttons */}
                    <Col md={12} className="d-flex justify-content-center gap-2 mt-3">
                        <button
                            className="btn-cancel"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>

                        <button
                            className="btn-submit"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </Col>
                </Row>
            </div>
        </Container>
    );
}
